import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Building2, Upload, Mail, Phone, MapPin, Globe, Clock, Calendar,
  ChevronRight, ChevronLeft, Check, Users, Briefcase, BarChart3,
  ClipboardList, UserPlus, Target, CheckCircle2, Sparkles, X,
  DollarSign, FileText, PieChart, UserCheck, CalendarDays, Bell
} from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BUSINESS_CATEGORIES = [
  { id: "it", label: "IT Company" },
  { id: "education", label: "Education" },
  { id: "healthcare", label: "Healthcare" },
  { id: "real_estate", label: "Real Estate" },
  { id: "retail", label: "Retail" },
  { id: "food", label: "Food & Restaurant" },
  { id: "finance", label: "Finance & Banking" },
  { id: "fitness", label: "Fitness & Wellness" },
  { id: "other", label: "Other" },
];

// Available modules with icons and descriptions
const AVAILABLE_MODULES = [
  {
    id: "attendance",
    name: "Attendance",
    description: "Track employee punch-in/out, leaves & overtime",
    icon: Clock,
    color: "emerald",
    default: true
  },
  {
    id: "payroll",
    name: "Payroll",
    description: "Manage salaries, deductions & payslips",
    icon: DollarSign,
    color: "blue",
    default: false
  },
  {
    id: "crm",
    name: "CRM",
    description: "Manage leads, contacts & deals",
    icon: Target,
    color: "purple",
    default: false
  },
  {
    id: "leads",
    name: "Leads",
    description: "Track and manage sales leads",
    icon: UserPlus,
    color: "orange",
    default: false
  },
  {
    id: "clients",
    name: "Clients",
    description: "Client database & relationship management",
    icon: Briefcase,
    color: "cyan",
    default: false
  },
  {
    id: "tasks",
    name: "Tasks",
    description: "Task management & project tracking",
    icon: ClipboardList,
    color: "amber",
    default: false
  },
  {
    id: "leave",
    name: "Leave Management",
    description: "Leave requests, approvals & balance tracking",
    icon: CalendarDays,
    color: "rose",
    default: false
  },
  {
    id: "reports",
    name: "Reports & Analytics",
    description: "Performance dashboards & insights",
    icon: BarChart3,
    color: "indigo",
    default: true
  }
];

// Timezone options
const TIMEZONES = [
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "America/New_York", label: "US Eastern (EST)" },
  { value: "America/Los_Angeles", label: "US Pacific (PST)" },
  { value: "Europe/London", label: "UK (GMT)" },
  { value: "Europe/Paris", label: "Europe Central (CET)" },
  { value: "Asia/Dubai", label: "Dubai (GST)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Australia/Sydney", label: "Australia (AEST)" }
];

// Currency options
const CURRENCIES = [
  { value: "INR", label: "₹ Indian Rupee (INR)", symbol: "₹" },
  { value: "USD", label: "$ US Dollar (USD)", symbol: "$" },
  { value: "EUR", label: "€ Euro (EUR)", symbol: "€" },
  { value: "GBP", label: "£ British Pound (GBP)", symbol: "£" },
  { value: "AED", label: "د.إ UAE Dirham (AED)", symbol: "د.إ" },
  { value: "SGD", label: "S$ Singapore Dollar (SGD)", symbol: "S$" },
  { value: "AUD", label: "A$ Australian Dollar (AUD)", symbol: "A$" }
];

// Days of week
const WEEK_DAYS = [
  { id: "sunday", label: "Sun", full: "Sunday" },
  { id: "monday", label: "Mon", full: "Monday" },
  { id: "tuesday", label: "Tue", full: "Tuesday" },
  { id: "wednesday", label: "Wed", full: "Wednesday" },
  { id: "thursday", label: "Thu", full: "Thursday" },
  { id: "friday", label: "Fri", full: "Friday" },
  { id: "saturday", label: "Sat", full: "Saturday" }
];

const BusinessOnboardingWizard = ({ open, onClose, onSuccess }) => {
  const { refreshBusinesses, setSelectedBusinessId } = useBusiness();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdBusinessId, setCreatedBusinessId] = useState(null);
  const [animationDirection, setAnimationDirection] = useState("forward");
  
  // Step 1: Business Details
  const [businessDetails, setBusinessDetails] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    industry: "",
    timezone: "Asia/Kolkata",
    currency: "INR",
    workingDays: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday"],
    officeStartTime: "09:00",
    officeEndTime: "18:00",
    logo: null,
    logoPreview: null
  });
  
  // Step 2: Selected Modules
  const [selectedModules, setSelectedModules] = useState(
    AVAILABLE_MODULES.filter(m => m.default).map(m => m.id)
  );
  
  // Step 3: Team Members
  const [teamMembers, setTeamMembers] = useState([]);
  const [newMember, setNewMember] = useState({
    name: "",
    email: "",
    phone: "",
    role: "employee"
  });
  const [inviteEmails, setInviteEmails] = useState("");
  
  // Validation errors
  const [errors, setErrors] = useState({});

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentStep(1);
      setCreatedBusinessId(null);
      setBusinessDetails({
        name: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        industry: "",
        timezone: "Asia/Kolkata",
        currency: "INR",
        workingDays: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday"],
        officeStartTime: "09:00",
        officeEndTime: "18:00",
        logo: null,
        logoPreview: null
      });
      setSelectedModules(AVAILABLE_MODULES.filter(m => m.default).map(m => m.id));
      setTeamMembers([]);
      setNewMember({ name: "", email: "", phone: "", role: "employee" });
      setInviteEmails("");
      setErrors({});
    }
  }, [open]);

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Logo must be less than 2MB");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setBusinessDetails(prev => ({
          ...prev,
          logo: file,
          logoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Toggle working day
  const toggleWorkingDay = (dayId) => {
    setBusinessDetails(prev => ({
      ...prev,
      workingDays: prev.workingDays.includes(dayId)
        ? prev.workingDays.filter(d => d !== dayId)
        : [...prev.workingDays, dayId]
    }));
  };

  // Toggle module
  const toggleModule = (moduleId) => {
    setSelectedModules(prev => 
      prev.includes(moduleId)
        ? prev.filter(m => m !== moduleId)
        : [...prev, moduleId]
    );
  };

  // Validate step 1
  const validateStep1 = () => {
    const newErrors = {};
    
    if (!businessDetails.name.trim()) {
      newErrors.name = "Business name is required";
    }
    if (!businessDetails.email.trim()) {
      newErrors.email = "Business email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(businessDetails.email)) {
      newErrors.email = "Invalid email format";
    }
    if (businessDetails.workingDays.length === 0) {
      newErrors.workingDays = "Select at least one working day";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate step 2
  const validateStep2 = () => {
    if (selectedModules.length === 0) {
      toast.error("Please select at least one module");
      return false;
    }
    return true;
  };

  // Add team member
  const addTeamMember = () => {
    if (!newMember.name.trim() || !newMember.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newMember.email)) {
      toast.error("Invalid email format");
      return;
    }
    
    if (teamMembers.some(m => m.email.toLowerCase() === newMember.email.toLowerCase())) {
      toast.error("This email is already added");
      return;
    }
    
    setTeamMembers(prev => [...prev, { ...newMember, id: Date.now() }]);
    setNewMember({ name: "", email: "", phone: "", role: "employee" });
  };

  // Remove team member
  const removeTeamMember = (id) => {
    setTeamMembers(prev => prev.filter(m => m.id !== id));
  };

  // Navigate to next step
  const nextStep = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    
    setAnimationDirection("forward");
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  // Navigate to previous step
  const prevStep = () => {
    setAnimationDirection("backward");
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Submit and create business
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Step 1: Create the business
      const selectedCat = BUSINESS_CATEGORIES.find(c => c.label === businessDetails.industry);
      const businessPayload = {
        name: businessDetails.name,
        city: businessDetails.city,
        industry: businessDetails.industry,
        business_category: selectedCat ? selectedCat.id : businessDetails.industry.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
        email: businessDetails.email,
        phone: businessDetails.phone,
        address: businessDetails.address,
        timezone: businessDetails.timezone,
        currency: businessDetails.currency,
        working_days: businessDetails.workingDays,
        office_start_time: businessDetails.officeStartTime,
        office_end_time: businessDetails.officeEndTime,
        enabled_modules: selectedModules,
        status: "active"
      };
      
      const businessResponse = await fetch(`${BACKEND_URL}/api/businesses/onboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(businessPayload)
      });
      
      if (!businessResponse.ok) {
        const error = await businessResponse.json();
        throw new Error(error.detail || "Failed to create business");
      }
      
      const businessData = await businessResponse.json();
      const newBusinessId = businessData.business_id;
      setCreatedBusinessId(newBusinessId);
      
      // Step 2: Upload logo if provided
      if (businessDetails.logo) {
        const formData = new FormData();
        formData.append("logo", businessDetails.logo);
        
        await fetch(`${BACKEND_URL}/api/businesses/${newBusinessId}/logo`, {
          method: "POST",
          credentials: "include",
          body: formData
        });
      }
      
      // Step 3: Add team members if any
      for (const member of teamMembers) {
        await fetch(`${BACKEND_URL}/api/businesses/${newBusinessId}/invite`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            name: member.name,
            email: member.email,
            phone: member.phone,
            role: member.role
          })
        });
      }
      
      // Step 4: Send email invites if any
      if (inviteEmails.trim()) {
        const emails = inviteEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e);
        if (emails.length > 0) {
          await fetch(`${BACKEND_URL}/api/businesses/${newBusinessId}/send-invites`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ emails })
          });
        }
      }
      
      // Move to completion step
      setCurrentStep(4);
      
      // Trigger confetti
      setTimeout(() => {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      }, 300);
      
      // Refresh businesses list
      await refreshBusinesses();
      
    } catch (error) {
      console.error("Onboarding error:", error);
      toast.error(error.message || "Failed to complete onboarding");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle completion
  const handleGoToDashboard = () => {
    if (createdBusinessId) {
      setSelectedBusinessId(createdBusinessId);
    }
    onSuccess?.();
    onClose();
  };

  const handleAddAnother = () => {
    setCurrentStep(1);
    setCreatedBusinessId(null);
    setBusinessDetails({
      name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      industry: "",
      timezone: "Asia/Kolkata",
      currency: "INR",
      workingDays: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday"],
      officeStartTime: "09:00",
      officeEndTime: "18:00",
      logo: null,
      logoPreview: null
    });
    setSelectedModules(AVAILABLE_MODULES.filter(m => m.default).map(m => m.id));
    setTeamMembers([]);
    setInviteEmails("");
  };

  // Get module color classes
  const getModuleColorClasses = (color, isSelected) => {
    const colors = {
      emerald: isSelected ? "bg-emerald-500 text-white border-emerald-500" : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:border-emerald-400",
      blue: isSelected ? "bg-blue-500 text-white border-blue-500" : "bg-blue-50 text-blue-700 border-blue-200 hover:border-blue-400",
      purple: isSelected ? "bg-purple-500 text-white border-purple-500" : "bg-purple-50 text-purple-700 border-purple-200 hover:border-purple-400",
      orange: isSelected ? "bg-orange-500 text-white border-orange-500" : "bg-orange-50 text-orange-700 border-orange-200 hover:border-orange-400",
      cyan: isSelected ? "bg-cyan-500 text-white border-cyan-500" : "bg-cyan-50 text-cyan-700 border-cyan-200 hover:border-cyan-400",
      amber: isSelected ? "bg-amber-500 text-white border-amber-500" : "bg-amber-50 text-amber-700 border-amber-200 hover:border-amber-400",
      rose: isSelected ? "bg-rose-500 text-white border-rose-500" : "bg-rose-50 text-rose-700 border-rose-200 hover:border-rose-400",
      indigo: isSelected ? "bg-indigo-500 text-white border-indigo-500" : "bg-indigo-50 text-indigo-700 border-indigo-200 hover:border-indigo-400"
    };
    return colors[color] || colors.emerald;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-2xl p-0 gap-0 overflow-hidden bg-white dark:bg-slate-900 border-0 shadow-2xl rounded-2xl"
        data-testid="business-onboarding-wizard"
      >
        {/* Progress Header */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
              {currentStep === 1 && "Let's Set Up Your Business"}
              {currentStep === 2 && "Select Modules You Need"}
              {currentStep === 3 && "Invite Your Team"}
              {currentStep === 4 && "You're All Set!"}
            </h2>
            <button 
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
          
          {/* Step Indicator */}
          {currentStep < 4 && (
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                    step < currentStep 
                      ? "bg-emerald-500 text-white" 
                      : step === currentStep 
                        ? "bg-indigo-500 text-white ring-4 ring-indigo-100 dark:ring-indigo-900" 
                        : "bg-slate-100 text-slate-400 dark:bg-slate-800"
                  }`}>
                    {step < currentStep ? <Check className="w-4 h-4" /> : step}
                  </div>
                  {step < 3 && (
                    <div className={`w-12 sm:w-20 h-1 mx-1 rounded-full transition-all duration-300 ${
                      step < currentStep ? "bg-emerald-500" : "bg-slate-100 dark:bg-slate-800"
                    }`} />
                  )}
                </div>
              ))}
              <span className="ml-2 text-sm text-slate-500">Step {currentStep} of 3</span>
            </div>
          )}
        </div>

        {/* Content Area with Animation */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* Step 1: Business Details */}
          {currentStep === 1 && (
            <div className={`space-y-5 animate-in ${animationDirection === "forward" ? "slide-in-from-right-5" : "slide-in-from-left-5"} duration-300`}>
              {/* Logo Upload */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className={`w-20 h-20 rounded-xl border-2 border-dashed ${
                    businessDetails.logoPreview ? "border-emerald-300" : "border-slate-200 dark:border-slate-700"
                  } flex items-center justify-center overflow-hidden bg-slate-50 dark:bg-slate-800`}>
                    {businessDetails.logoPreview ? (
                      <img src={businessDetails.logoPreview} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <Building2 className="w-8 h-8 text-slate-300" />
                    )}
                  </div>
                  <label className="absolute -bottom-1 -right-1 p-1.5 bg-indigo-500 rounded-lg cursor-pointer hover:bg-indigo-600 transition-colors">
                    <Upload className="w-3.5 h-3.5 text-white" />
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  </label>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Business Logo</p>
                  <p className="text-xs text-slate-500">PNG, JPG up to 2MB</p>
                </div>
              </div>

              {/* Business Name & Email Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Business Name *</Label>
                  <div className="relative mt-1">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={businessDetails.name}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Acme Corporation"
                      className={`pl-10 ${errors.name ? "border-red-500" : ""}`}
                      data-testid="business-name-input"
                    />
                  </div>
                  {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Business Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="email"
                      value={businessDetails.email}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="contact@acme.com"
                      className={`pl-10 ${errors.email ? "border-red-500" : ""}`}
                      data-testid="business-email-input"
                    />
                  </div>
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>
              </div>

              {/* Phone & Address Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Phone</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={businessDetails.phone}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">City</Label>
                  <div className="relative mt-1">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={businessDetails.city}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="Mumbai"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              {/* Business Category */}
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Business Category *</Label>
                <Select
                  value={businessDetails.industry}
                  onValueChange={(v) => setBusinessDetails(prev => ({ ...prev, industry: v }))}
                >
                  <SelectTrigger className="mt-1" data-testid="industry-select">
                    <Briefcase className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.label}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.industry && <p className="text-xs text-red-500 mt-1">{errors.industry}</p>}
              </div>

              {/* Timezone & Currency Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Timezone</Label>
                  <Select 
                    value={businessDetails.timezone} 
                    onValueChange={(v) => setBusinessDetails(prev => ({ ...prev, timezone: v }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="timezone-select">
                      <Globe className="w-4 h-4 mr-2 text-slate-400" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Currency</Label>
                  <Select 
                    value={businessDetails.currency} 
                    onValueChange={(v) => setBusinessDetails(prev => ({ ...prev, currency: v }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Working Days */}
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Working Days *</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {WEEK_DAYS.map(day => (
                    <button
                      key={day.id}
                      type="button"
                      onClick={() => toggleWorkingDay(day.id)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        businessDetails.workingDays.includes(day.id)
                          ? "bg-indigo-500 text-white shadow-md"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400"
                      }`}
                      data-testid={`working-day-${day.id}`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
                {errors.workingDays && <p className="text-xs text-red-500 mt-1">{errors.workingDays}</p>}
              </div>

              {/* Office Hours */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Office Start Time</Label>
                  <div className="relative mt-1">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="time"
                      value={businessDetails.officeStartTime}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, officeStartTime: e.target.value }))}
                      className="pl-10"
                      data-testid="office-start-time"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Office End Time</Label>
                  <div className="relative mt-1">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="time"
                      value={businessDetails.officeEndTime}
                      onChange={(e) => setBusinessDetails(prev => ({ ...prev, officeEndTime: e.target.value }))}
                      className="pl-10"
                      data-testid="office-end-time"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Module Selection */}
          {currentStep === 2 && (
            <div className={`space-y-4 animate-in ${animationDirection === "forward" ? "slide-in-from-right-5" : "slide-in-from-left-5"} duration-300`}>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Select the modules you want to enable for your business. You can always change this later.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {AVAILABLE_MODULES.map(module => {
                  const isSelected = selectedModules.includes(module.id);
                  const Icon = module.icon;
                  
                  return (
                    <button
                      key={module.id}
                      type="button"
                      onClick={() => toggleModule(module.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${getModuleColorClasses(module.color, isSelected)}`}
                      data-testid={`module-${module.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${isSelected ? "bg-white/20" : "bg-white dark:bg-slate-900"}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{module.name}</p>
                            {isSelected && <Check className="w-4 h-4" />}
                          </div>
                          <p className={`text-xs mt-0.5 ${isSelected ? "text-white/80" : "opacity-70"}`}>
                            {module.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              
              <div className="flex items-center gap-2 pt-2 text-sm text-slate-500">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>{selectedModules.length} modules selected</span>
              </div>
            </div>
          )}

          {/* Step 3: Invite Team */}
          {currentStep === 3 && (
            <div className={`space-y-5 animate-in ${animationDirection === "forward" ? "slide-in-from-right-5" : "slide-in-from-left-5"} duration-300`}>
              {/* Add User Manually */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  Add User Manually
                </h4>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    placeholder="Full Name"
                    value={newMember.name}
                    onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="new-member-name"
                  />
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={newMember.email}
                    onChange={(e) => setNewMember(prev => ({ ...prev, email: e.target.value }))}
                    data-testid="new-member-email"
                  />
                  <Input
                    placeholder="Phone (Optional)"
                    value={newMember.phone}
                    onChange={(e) => setNewMember(prev => ({ ...prev, phone: e.target.value }))}
                  />
                  <Select 
                    value={newMember.role} 
                    onValueChange={(v) => setNewMember(prev => ({ ...prev, role: v }))}
                  >
                    <SelectTrigger data-testid="new-member-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button
                  type="button"
                  onClick={addTeamMember}
                  variant="outline"
                  className="mt-3"
                  data-testid="add-member-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add to Team
                </Button>
              </div>

              {/* Added Members List */}
              {teamMembers.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Team Members ({teamMembers.length})
                  </h4>
                  {teamMembers.map(member => (
                    <div 
                      key={member.id}
                      className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                          <Users className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{member.name}</p>
                          <p className="text-xs text-slate-500">{member.email} • {member.role}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeTeamMember(member.id)}
                        className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Invite via Email */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Invite via Email
                </h4>
                <Textarea
                  placeholder="Enter email addresses separated by commas or new lines&#10;e.g., john@example.com, jane@example.com"
                  value={inviteEmails}
                  onChange={(e) => setInviteEmails(e.target.value)}
                  rows={3}
                  className="resize-none"
                  data-testid="invite-emails"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Invitations will be sent after business creation
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Completion */}
          {currentStep === 4 && (
            <div className="flex flex-col items-center justify-center py-8 text-center animate-in fade-in zoom-in duration-500">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mb-6 shadow-lg shadow-emerald-200 dark:shadow-emerald-900/30">
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
              
              <h3 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
                Your Business is Ready!
              </h3>
              <p className="text-slate-500 dark:text-slate-400 max-w-sm">
                You can now start managing your team efficiently. Everything is set up and ready to go.
              </p>
              
              <div className="flex items-center gap-2 mt-6 px-4 py-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-full">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                <span className="text-sm text-emerald-700 dark:text-emerald-400">
                  {businessDetails.name} created successfully
                </span>
              </div>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4 mt-8 w-full max-w-sm">
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-xl text-center">
                  <p className="text-2xl font-bold text-indigo-600">{selectedModules.length}</p>
                  <p className="text-xs text-slate-500">Modules Enabled</p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-xl text-center">
                  <p className="text-2xl font-bold text-indigo-600">{teamMembers.length}</p>
                  <p className="text-xs text-slate-500">Team Members</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
          {currentStep < 4 ? (
            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={currentStep === 1 ? onClose : prevStep}
                data-testid="wizard-back-btn"
              >
                {currentStep === 1 ? "Cancel" : (
                  <>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back
                  </>
                )}
              </Button>
              
              <div className="flex items-center gap-2">
                {currentStep === 3 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    data-testid="wizard-skip-btn"
                  >
                    Skip & Finish
                  </Button>
                )}
                
                <Button
                  type="button"
                  onClick={currentStep === 3 ? handleSubmit : nextStep}
                  disabled={isSubmitting}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white"
                  data-testid="wizard-next-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Creating...
                    </>
                  ) : currentStep === 3 ? (
                    <>
                      Finish Setup
                      <Check className="w-4 h-4 ml-2" />
                    </>
                  ) : (
                    <>
                      Continue
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleAddAnother}
                data-testid="add-another-business-btn"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Add Another Business
              </Button>
              <Button
                type="button"
                onClick={handleGoToDashboard}
                className="bg-indigo-500 hover:bg-indigo-600 text-white"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BusinessOnboardingWizard;
